/**
 * Build an image URL with optional COS imageView2 processing params.
 * Works with both public CDN URLs and signed CDN URLs.
 *
 * @param baseUrl - The base image URL (from backend API)
 * @param options - Optional image processing: w (width), h (height), q (quality 1-100)
 * @returns Final URL with imageView2 params appended if options provided
 *
 * @example
 * // Original image
 * buildImageUrl('https://cdn.app.com/uploads/1/abc.jpg?sign=...')
 *
 * // 200x200 thumbnail
 * buildImageUrl('https://cdn.app.com/uploads/1/abc.jpg?sign=...', { w: 200, h: 200 })
 *
 * // Width-constrained with custom quality
 * buildImageUrl('https://cdn.app.com/uploads/1/abc.jpg', { w: 800, q: 90 })
 */
export function buildImageUrl(
  baseUrl: string,
  options?: { w?: number; h?: number; q?: number },
): string {
  if (!baseUrl) return '';
  if (!options || (!options.w && !options.h)) return baseUrl;

  const parts: string[] = [];
  // imageView2/1 = scale and crop to fit, /2 = scale to fit within bounds
  const mode = options.w && options.h ? '1' : '2';
  parts.push(`imageView2/${mode}`);
  if (options.w) parts.push(`w/${options.w}`);
  if (options.h) parts.push(`h/${options.h}`);
  parts.push(`q/${options.q || 85}`);

  const rule = parts.join('/');
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${rule}`;
}
